<template>
  <div>
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-gear me-2"></i>System Settings
      </h4>
      <button class="btn btn-outline-dark" @click="$router.go(-1)">
        <i class="bi bi-arrow-left me-1"></i>Back
      </button>
    </div>

    <!-- Online Sale Notification Emails Section -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card shadow-sm">
          <div class="card-header bg-light d-flex justify-content-between align-items-center">
            <h4 class="card-title m-0">
              <i class="bi bi-envelope me-2 text-primary"></i>Online Sale Notification Emails
            </h4>
            <button class="btn btn-primary btn-sm" @click="openEmailModal()">
              <i class="bi bi-plus-lg me-1"></i> Add Email
            </button>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                <tr>
                  <th scope="col" class="ps-4">Email Address</th>
                  <th scope="col">Added On</th>
                  <th scope="col" class="text-center">Actions</th>
                </tr>
                </thead>
                <tbody v-if="!loadingEmails">
                <tr v-for="email in notificationEmails" :key="email.id">
                  <td class="ps-4">
                    <div class="d-flex align-items-center">
                      <i class="fas fa-envelope text-primary me-2"></i>
                      <strong>{{ email.value }}</strong>
                    </div>
                  </td>
                  <td>
                    <span class="text-muted">{{ formatDate(email.created_at) }}</span>
                  </td>
                  <td class="text-center">
                    <button
                      class="btn btn-outline-primary btn-sm me-2"
                      @click="openEmailModal(email)"
                    >
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button
                      class="btn btn-outline-danger btn-sm"
                      @click="deleteEmail(email.id)"
                    >
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="3" class="text-center py-4">
                      <Spinner spinner-class="mb-1" />
                    </td>
                  </tr>
                </tbody>
                <tbody v-if="!loadingEmails && notificationEmails.length === 0">
                  <tr>
                    <td colspan="3" class="text-center py-4 text-muted">
                      <i class="fas fa-info-circle me-2"></i>No notification emails configured
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Store Authorization Settings (Cart Actions) Section -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card shadow-sm">
          <div class="card-header bg-light d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div class="d-flex align-items-center flex-wrap gap-3">
              <h4 class="card-title m-0">
                <i class="bi bi-shield-lock me-2 text-primary"></i>Store Authorization Settings (Cart Actions)
              </h4>
              <div v-if="stores.length > 0" class="d-flex align-items-center gap-2">
                <label class="form-label m-0 text-muted small fw-bold text-nowrap">Store:</label>
                <select
                  class="form-select form-select-sm"
                  v-model="selectedStoreId"
                  @change="getAuthorizedUsers"
                  style="min-width: 180px;"
                >
                  <option v-for="s in stores" :key="s.id" :value="s.id">
                    {{ s.name }} ({{ s.code }})
                  </option>
                </select>
              </div>
            </div>
            <button
              v-if="selectedStoreId && (!profile.role || (profile.role !== 'user' && profile.role !== 'cashier'))"
              class="btn btn-primary btn-sm"
              @click="openAuthorizedUserModal"
            >
              <i class="bi bi-person-plus me-1"></i> Add Authorized User
            </button>
          </div>
          <div class="card-body p-0">
            <div class="px-4 py-3 bg-light border-bottom">
              <p class="text-muted small mb-0">
                <i class="bi bi-info-circle me-1 text-primary"></i>
                Users added here are authorized to receive 4-digit OTP codes when a cashier attempts to reduce product quantities or remove items from the POS cart in this store. If no store-specific users are configured, organization primary and admin users will receive authorization requests by default.
              </p>
            </div>
            <div class="table-responsive">
              <table class="table table-hover align-middle mb-0">
                <thead class="table-light">
                <tr>
                  <th scope="col" class="ps-4">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Phone</th>
                  <th scope="col">Role</th>
                  <th scope="col" class="text-center" v-if="!profile.role || (profile.role !== 'user' && profile.role !== 'cashier')">Actions</th>
                </tr>
                </thead>
                <tbody v-if="!loadingAuthorizedUsers && stores.length > 0 && authorizedUsers.length > 0">
                <tr v-for="user in authorizedUsers" :key="user.id">
                  <td class="ps-4">
                    <div class="d-flex align-items-center">
                      <i class="bi bi-person-circle text-primary me-2"></i>
                      <strong>{{ user.first_name }} {{ user.last_name }}</strong>
                    </div>
                  </td>
                  <td>{{ user.email }}</td>
                  <td>{{ user.phone || '-' }}</td>
                  <td>
                    <span class="badge bg-light text-dark border text-capitalize">{{ user.role }}</span>
                  </td>
                  <td class="text-center" v-if="!profile.role || (profile.role !== 'user' && profile.role !== 'cashier')">
                    <button
                      class="btn btn-outline-danger btn-sm"
                      @click="removeAuthorizedUser(user.id)"
                      title="Remove Authorization"
                    >
                      <i class="bi bi-trash me-1"></i> Remove
                    </button>
                  </td>
                </tr>
                </tbody>
                <tbody v-else-if="loadingAuthorizedUsers">
                  <tr>
                    <td colspan="5" class="text-center py-4">
                      <Spinner spinner-class="mb-1" />
                      <p class="text-muted small mt-2 mb-0">Loading authorized users...</p>
                    </td>
                  </tr>
                </tbody>
                <tbody v-if="!loadingAuthorizedUsers && stores.length === 0">
                  <tr>
                    <td colspan="5" class="text-center py-4 text-muted">
                      <i class="fas fa-info-circle me-2"></i>No stores configured in your organization
                    </td>
                  </tr>
                </tbody>
                <tbody v-else-if="!loadingAuthorizedUsers && stores.length > 0 && authorizedUsers.length === 0">
                  <tr>
                    <td colspan="5" class="text-center py-4 text-muted">
                      <i class="bi bi-shield-check text-muted d-block mb-2" style="font-size: 1.75rem;"></i>
                      <span>No store-specific authorized users configured for this store.</span>
                      <div class="mt-2" v-if="!profile.role || (profile.role !== 'user' && profile.role !== 'cashier')">
                        <button class="btn btn-sm btn-outline-primary" @click="openAuthorizedUserModal">
                          <i class="bi bi-person-plus me-1"></i> Add Authorized User
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- General Settings Section -->
    <div class="row">
      <div class="col-12">
        <div class="card shadow-sm">
          <div class="card-header bg-light d-flex justify-content-between align-items-center">
            <h4 class="card-title m-0">
              <i class="bi bi-box-seam me-2 text-primary"></i>Product Settings
            </h4>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover table-striped mb-0">
                <thead class="table-light">
                <tr>
                  <th scope="col" class="ps-4">Setting Name</th>
                  <th scope="col">Value</th>
                  <th scope="col">Description</th>
                  <th scope="col" class="text-center">Actions</th>
                </tr>
                </thead>
                <tbody v-if="!loading">
                <tr v-for="setting in filteredSettings" :key="setting.id">
                  <td class="ps-4">
                    <div class="d-flex align-items-center">
                      <i class="fas fa-sliders-h text-primary me-2"></i>
                      <strong>{{ setting.name }}</strong>
                    </div>
                  </td>
                  <td>
                    <span class="badge bg-light text-dark border">{{ setting.value }}</span>
                  </td>
                  <td>
                    <span class="text-muted">{{ setting.description }}</span>
                  </td>
                  <td class="text-center">
                    <button
                      class="btn btn-outline-primary btn-sm"
                      @click="selectedSetting(setting); settingModal = true"
                    >
                      <i class="fas fa-edit me-1"></i> Edit
                    </button>
                  </td>
                </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="4" class="text-center py-4">
                      <Spinner spinner-class="mb-1" />
                    </td>
                  </tr>
                </tbody>
                <tbody v-if="!loading && filteredSettings.length === 0">
                  <tr>
                    <td colspan="4" class="text-center py-4 text-muted">
                      <i class="fas fa-info-circle me-2"></i>No settings found matching your search
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Setting Modal -->
    <Modal :open="settingModal" @closed="settingModal = false">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="fas fa-edit me-2 text-primary"></i>Edit Setting
        </h5>
        <button @click="settingModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <div class="card mb-3">
          <div class="card-body bg-light">
            <div class="d-flex align-items-center">
              <i class="fas fa-info-circle text-primary me-2 fs-5"></i>
              <div>
                <h6 class="mb-1">{{ setting.name }}</h6>
                <p class="mb-0 text-muted small">{{ setting.description }}</p>
              </div>
            </div>
          </div>
        </div>
        <form @submit.prevent="updateSetting">
          <div class="mb-3">
            <label class="form-label">Value</label>
            <input
              type="text"
              class="form-control"
              placeholder="Enter value"
              v-model="setting.value"
              autofocus
            >
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button
          type="button"
          class="btn btn-outline-secondary"
          data-dismiss="modal"
          @click="settingModal = false"
        >
          <i class="fas fa-times me-1"></i> Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          @click="updateSetting"
        >
          <i class="fas fa-save me-1"></i> Save Changes
        </button>
      </div>
    </Modal>
    <!-- Edit Setting Modal End -->

    <!-- Notification Email Modal -->
    <Modal :open="emailModal" @closed="emailModal = false">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="fas fa-envelope me-2 text-primary"></i>{{ emailForm.id ? 'Edit' : 'Add' }} Notification Email
        </h5>
        <button @click="emailModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <div class="alert alert-info" role="alert">
          <i class="fas fa-info-circle me-2"></i>
          <strong>Info:</strong> Emails added here will receive notifications for online sales.
        </div>
        <form @submit.prevent="saveEmail">
          <div class="mb-3">
            <label class="form-label">Email Address <span class="text-danger">*</span></label>
            <input
              type="email"
              class="form-control"
              placeholder="Enter email address"
              v-model="emailForm.email"
              required
              autofocus
            >
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button
          type="button"
          class="btn btn-outline-secondary"
          data-dismiss="modal"
          @click="emailModal = false"
        >
          <i class="fas fa-times me-1"></i> Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          @click="saveEmail"
        >
          <i class="fas fa-save me-1"></i> {{ emailForm.id ? 'Update' : 'Add' }} Email
        </button>
      </div>
    </Modal>
    <!-- Notification Email Modal End -->

    <!-- Add Store Authorized User Modal -->
    <Modal :open="authorizedUserModal" @closed="authorizedUserModal = false" dialog-size="modal-md">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="bi bi-person-plus me-2 text-primary"></i>Add Authorized User
        </h5>
        <button @click="authorizedUserModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <p class="text-muted small mb-3">
          Select a user from your organization to authorize for cart quantity reductions and product deletions in <strong>{{ currentStoreName }}</strong>.
        </p>
        <div v-if="loadingOrgUsers" class="text-center py-3">
          <Spinner spinner-class="mb-1" />
          <p class="text-muted small">Loading users...</p>
        </div>
        <div v-else class="form-group">
          <label class="form-label fw-bold">Select User:</label>
          <select class="form-select" v-model="selectedUserId">
            <option value="" disabled selected>-- Select a user --</option>
            <option
              v-for="user in availableUsers"
              :key="user.id"
              :value="user.id"
            >
              {{ user.first_name }} {{ user.last_name }} ({{ user.email }}) - {{ user.role }}
            </option>
          </select>
          <div v-if="availableUsers.length === 0" class="alert alert-warning mt-2 py-2 small">
            All active users in your organization are already authorized for this store.
          </div>
        </div>
      </div>
      <div class="modal-footer bg-light">
        <button
          type="button"
          class="btn btn-outline-secondary"
          data-dismiss="modal"
          @click="authorizedUserModal = false"
        >
          <i class="fas fa-times me-1"></i> Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!selectedUserId || addingAuthorizedUser"
          @click="addAuthorizedUser"
        >
          <i class="bi bi-check-circle me-1"></i> Add User
        </button>
      </div>
    </Modal>
    <!-- Add Store Authorized User Modal End -->

  </div>
</template>

<script>
import settingService from "@/api/settingService";
import storeService from "@/api/storeService";
import userService from "@/api/userService";
import loadingButton from "@/utils/loadingButton";
import notify from "@/utils/notify";
import { decrypt } from "@/utils/crypto";

export default {
  name: "Settings",

  data() {
    return {
      profile: {},
      loading: true,
      loadingEmails: true,
      settingModal: false,
      emailModal: false,
      searchQuery: '',
      settings: [],
      notificationEmails: [],
      setting: {
        id: '',
        name: '',
        value: '',
        description: ''
      },
      emailForm: {
        id: null,
        email: '',
        name: 'onlineSaleNotificationEmail'
      },
      stores: [],
      selectedStoreId: '',
      authorizedUsers: [],
      loadingAuthorizedUsers: false,
      authorizedUserModal: false,
      selectedUserId: '',
      organizationUsers: [],
      loadingOrgUsers: false,
      addingAuthorizedUser: false
    }
  },

  computed: {
    filteredSettings() {
      if (!this.searchQuery) return this.settings;

      const query = this.searchQuery.toLowerCase();
      return this.settings.filter(setting =>
        setting.name.toLowerCase().includes(query) ||
        setting.value.toLowerCase().includes(query) ||
        (setting.description && setting.description.toLowerCase().includes(query))
      );
    },
    currentStoreName() {
      const store = this.stores.find(s => s.id === this.selectedStoreId);
      return store ? store.name : 'Selected Store';
    },
    availableUsers() {
      return this.organizationUsers.filter(u =>
        !this.authorizedUsers.some(au => au.id === u.id) && u.status === 'active'
      );
    }
  },

  methods: {
    async getSettings() {
      const response = await settingService.getSettings()
      if (response) {
        // Filter out notification emails from general settings
        this.notificationEmails = response.filter(s => s.name === 'onlineSaleNotificationEmail')
        this.settings = response.filter(s => s.name !== 'onlineSaleNotificationEmail')
      }
      this.loading = false
      this.loadingEmails = false
    },
    async getStores() {
      try {
        const res = await storeService.getStores();
        this.stores = res || [];
        if (this.stores.length > 0 && !this.selectedStoreId) {
          try {
            const sessionStoreStr = sessionStorage.getItem('store');
            if (sessionStoreStr) {
              const sessionStore = JSON.parse(decrypt(sessionStoreStr));
              if (sessionStore && sessionStore.id && this.stores.some(s => s.id === sessionStore.id)) {
                this.selectedStoreId = sessionStore.id;
              }
            }
          } catch (e) {
            // ignore error reading session store
          }

          if (!this.selectedStoreId) {
            this.selectedStoreId = this.stores[0].id;
          }
          await this.getAuthorizedUsers();
        }
      } catch (error) {
        console.error("Error fetching stores:", error);
      }
    },
    async getAuthorizedUsers() {
      if (!this.selectedStoreId) return;
      this.loadingAuthorizedUsers = true;
      try {
        this.authorizedUsers = await storeService.getAuthorizedUsers(this.selectedStoreId);
      } catch (error) {
        console.error("Error fetching authorized users:", error);
      } finally {
        this.loadingAuthorizedUsers = false;
      }
    },
    async openAuthorizedUserModal() {
      this.selectedUserId = '';
      this.authorizedUserModal = true;
      if (this.organizationUsers.length === 0) {
        try {
          this.loadingOrgUsers = true;
          const response = await userService.getUsers({ per_page: 100 });
          this.organizationUsers = response?.data || response || [];
        } catch (error) {
          console.error("Error fetching organization users:", error);
        } finally {
          this.loadingOrgUsers = false;
        }
      }
    },
    async addAuthorizedUser(e) {
      if (!this.selectedUserId) {
        notify.alert.error('Please select a user to authorize');
        return;
      }
      try {
        this.addingAuthorizedUser = true;
        if (e && e.target) loadingButton(e);
        let response = await storeService.addAuthorizedUser(this.selectedStoreId, this.selectedUserId);
        if (response) {
          this.authorizedUserModal = false;
          this.selectedUserId = '';
          await this.getAuthorizedUsers();
        }
      } catch (error) {
        console.error("Error adding authorized user:", error);
      } finally {
        this.addingAuthorizedUser = false;
        if (e && e.target) loadingButton(e, false);
      }
    },
    async removeAuthorizedUser(userId) {
      if (!confirm('Are you sure you want to remove this user from authorized users?')) {
        return;
      }
      try {
        let response = await storeService.removeAuthorizedUser(this.selectedStoreId, userId);
        if (response) {
          await this.getAuthorizedUsers();
        }
      } catch (error) {
        console.error("Error removing authorized user:", error);
      }
    },
    selectedSetting(data) {
      this.setting = data
    },
    async updateSetting(e) {
      loadingButton(e)
      let response =await settingService.updateSetting(this.setting)
      if (response) {
        this.settingModal = false
        await this.getSettings()
      }
      loadingButton(e, false)
    },
    openEmailModal(email = null) {
      if (email) {
        this.emailForm = {
          id: email.id,
          email: email.value,
          name: 'onlineSaleNotificationEmail'
        }
      } else {
        this.emailForm = {
          id: null,
          email: '',
          name: 'onlineSaleNotificationEmail'
        }
      }
      this.emailModal = true
    },
    async saveEmail(e) {
      loadingButton(e)
      const payload = {
        id: this.emailForm.id,
        name: 'onlineSaleNotificationEmail',
        value: this.emailForm.email,
        description: 'Email address for online sale notifications'
      }
      const response = await settingService.createSetting(payload)
      if (response) {
        this.emailModal = false
        await this.getSettings()
      }
      loadingButton(e, false)
    },
    async deleteEmail(id) {
      if (confirm('Are you sure you want to delete this notification email?')) {
        const response = await settingService.deleteSetting(id)
        if (response) {
          await this.getSettings()
        }
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  },

  async created() {
    try {
      const profileStr = sessionStorage.getItem('profile');
      if (profileStr) {
        this.profile = JSON.parse(decrypt(profileStr));
      }
    } catch (e) {
      // ignore error decrypting profile
    }
    await Promise.all([
      this.getSettings(),
      this.getStores()
    ]);
  }
}
</script>

<style scoped>

</style>
